import * as React from 'react';
import * as UI from 'core/ui';
import { AcceptActionButton, BackActionButton, Content, Filler, Footer, Header, LargeDeviceOnly, PhoneOnly, PosScreen } from 'pos/ui';
import { Res } from 'core/api';
import { Result, SessionProvider } from 'core/infrastructure';
import { CustomerDisplay, Scale } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';
import { VStack, Text, Box, Span, HStack } from '@chakra-ui/react';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { BasicButton } from 'core/ui-next';
import { z } from 'zod';
import { zDecimalToString } from 'core/infrastructure-next';

const formatMoney = UI.Format.asMoney;

type Props = {
	productName: string,
	price: number,
	isScaleEnabled: boolean
} & Modal.Actions<number>;

const createStateSchema = (isScaleEnabled: boolean) => z.object({
	kgs: zDecimalToString({ strictPositive: true, required: true, decimalPlaces: 3 }),
	isStable: z.boolean()
}).superRefine((data, ctx) => {

	// Validar que si la balanza está habilitada, el peso debe estar estable
	if (isScaleEnabled && data.kgs && !data.isStable) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ['kgs'],
			message: Res.Web.OBTAIN_PRODUCT_WEIGHT_UNSTABLE_WEIGHT_LBL()
		});
	}
});

type State = z.infer<ReturnType<typeof createStateSchema>>;

function GetProductWeight(props: Props) {

	const { productName, price, isScaleEnabled, cancel, close } = props;
	
	const [kgs, setKgs] = React.useState<Nullable<number>>(null);
	const [isStable, setIsStable] = React.useState(false);
	const formRef = React.useRef<FormRef<State>>(null);
	const { Form, DecimalInput } = useControlsFor<State>();

	React.useEffect(() => {
		let stopScale: (() => void) | null = null;

		async function initializeScale() {
			if (isScaleEnabled) {
				stopScale = await Scale.start(status => {
					// Si no hay cambio de estado, evito hacer nada porque el remount implica un parpadeo feo de pantalla
					if (status.isStable === isStable && status.kgs === kgs)
						return;

					void CustomerDisplay.displayWeight(status.kgs || 0, price, status.kgs ? status.kgs * price : 0);

					setKgs(status.kgs);
					setIsStable(status.isStable);
				});
			}
		}

		void initializeScale();

		return () => {
			stopScale?.();
		};
	}, [isScaleEnabled, price, isStable, kgs]);

	const onValueChange = async () => {

		// Si está habilitado el Scale. El estado se cambia en onWeighRead de Scale.start
		if (isScaleEnabled)
			return;

		const valid = await formRef.current!.getValue();

		if (!valid.ok)
			return;

		setKgs(valid.value.kgs ? parseFloat(valid.value.kgs) : null);

	};

	const getTotalAmount = () => {
		return kgs ? kgs * price : 0;
	};

	const onAccept = async () => {
		const result = await formRef.current!.getValue();

		if (!result.ok)
			return;

		const isValidWeight = result.value.kgs !== null && parseFloat(result.value.kgs) > 0;
		if (!isValidWeight) {
			await Message.show(Res.Web.OBTAIN_PRODUCT_WEIGHT_TOTAL_ERROR_LBL());
			return;
		}

		close(parseFloat(result.value.kgs));
	};

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	React.useEffect(() => {
   	 	// Cuando isStable cambia, remonta el formulario para revalidar
    	formRef.current?.setValue('isStable', isStable);
		formRef.current?.setValue('kgs', kgs ? kgs.toString() : '');
	}, [isStable, kgs]);

	return <PosScreen screenId='GET_WEIGHT' size='small' useKeyPad={!isScaleEnabled}>
		<Header title={Res.Web.OBTAIN_PRODUCT_WEIGHT()} onClose={cancel} />
		<Content>
			<VStack paddingX={'6'} paddingY={'9'} h='full' w='full' gap={'6'}>
				<VStack gap={'4'} justifyItems={'center'} w='full'>
					<VStack gap={'2'} justifyItems={'center'} w='full' textAlign={'center'}>
						<Text fontSize={'sm'} fontWeight={'semibold'}>
							{Res.Web.OBTAIN_PRODUCT_WEIGHT_TITLE_LBL()}
						</Text>
						<Span fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{productName}</Span>
						<Span fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{formatMoney(price) + '/' + Res.Web.OBTAIN_PRODUCT_WEIGHT_KG_LBL()}</Span>
					</VStack>
				</VStack>
				<VStack w={'full'} justifyContent={'center'} gap={'6'}>
					<Box w={'full'}>
						<Form ref={formRef} initialValue={{ kgs: '', isStable: false }} schema={createStateSchema(isScaleEnabled)}>
							<DecimalInput 
								linkTo='kgs'
								placeholder={Res.Web.OBTAIN_PRODUCT_WEIGHT_WEIGHT_LBL()}
								onKeyDown={UI.whenEnterKey(onAccept)}
								onWatch={onValueChange}
								precision={3}
								disabled={isScaleEnabled}
							/>
						</Form>
					</Box>
					<HStack 
						w={'full'}
						borderTop={'4px solid'}
						borderColor={{ base: 'neutral.700', _dark: 'neutral.300' }}
						bg={{ base: 'white', _dark: 'neutral.800' }}
						paddingX={'2'}
						paddingY={'2.5'}
						justifyContent={'space-between'}
						shadow={'sm'}
					>
						<Text fontSize={'2xl'} fontWeight={'bold'}>{Res.Web.OBTAIN_PRODUCT_WEIGHT_TOTAL_LBL()}</Text>
						<Text fontSize={'2xl'} fontWeight={'bold'}>{formatMoney(getTotalAmount())}</Text>
					</HStack>
				</VStack>
				<LargeDeviceOnly>
					<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} />
				</LargeDeviceOnly>	
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				<AcceptActionButton action={onAccept} />
			</PhoneOnly>
		</Footer>
	</PosScreen>;
}

export async function getWeight(product: { SaleFormatName: string, Price: number }): Promise<Result<number>> {

	const status = await Scale.getWeight();
	if (status.isStable && status.kgs)
		return Result.ok(status.kgs);

	const result = await Modal.show(GetProductWeight, { productName: product.SaleFormatName, price: product.Price, isScaleEnabled: SessionProvider.getCurrent().UseScale });
	return result.ok ? Result.ok(result.value) : Result.error();
}